import type { KeyboardEvent, MouseEvent, SyntheticEvent } from 'react';
import React, { useRef, useState } from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { Image } from '@noths/polaris-client-ribbons-design-system';
import { getProductImageURL, isKeyboardTriggeredEvent } from '@noths/polaris-client-utils';

import { PRODUCT_IMAGES_SECTION_LABEL_PLURAL } from 'src/components/organisms/ProductImagesCarousel/constants/copy';
import {
  PRODUCT_IMAGES_TAB_ID_PREFIX,
  PRODUCT_IMAGES_TABPANEL_ID_PREFIX,
} from 'src/components/organisms/ProductImagesCarousel/constants/elementIds';
import type {
  ImagesLocation,
  NextOrPreviousPayload,
  ProductImagesState,
  SetCurrentImagePayload,
} from 'src/components/organisms/ProductImagesCarousel/types';
import { useThumbnailFocusAndScroll } from './useThumbnailFocusAndScroll';
import * as styles from './Thumbnails.styles';

interface ThumbnailImage {
  href: string;
  title: string;
}

export interface OnVisiblePayload {
  totalImages: number;
}

export interface ThumbnailsStateProps {
  currentImage: ProductImagesState['currentImage'];
  images: ThumbnailImage[];
}

export interface ThumbnailsDispatchProps {
  onNextImage: (payload: NextOrPreviousPayload) => void;
  onPrevImage: (payload: NextOrPreviousPayload) => void;
  onVisible: (payload: OnVisiblePayload) => void;
  setCurrentImage: (payload: SetCurrentImagePayload) => void;
}

interface ThumbnailsOwnProps {
  location: ImagesLocation;
}

type ThumbnailsProps = ThumbnailsStateProps & ThumbnailsDispatchProps & ThumbnailsOwnProps;

export const Thumbnails = ({
  currentImage,
  images,
  location,
  onNextImage,
  onPrevImage,
  onVisible,
  setCurrentImage,
}: ThumbnailsProps) => {
  const idPrefix = location.toLowerCase().replace(/\s/g, '');
  const totalImages = images.length;
  const productImagesCarouselRef = useImpressionRef<HTMLDivElement>(() => {
    onVisible({ totalImages });
  });
  const thumbnailRefs = useRef<HTMLAnchorElement[] | null[]>([]);
  const [thumbnailsHaveKeyboardFocus, setThumbnailsHaveKeyboardFocus] = useState(false);
  const prevImageKey = 'ArrowLeft';
  const nextImageKey = 'ArrowRight';
  const visibilityTrackingEnabled = location !== 'Expanded image';

  useThumbnailFocusAndScroll({
    thumbnailsHaveKeyboardFocus,
    thumbnailRefs,
    currentImage,
    shouldScrollThumbnailIntoView: location !== 'Carousel',
  });

  const handleKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    switch (event.key) {
      case prevImageKey:
        onPrevImage({ location, totalImages });
        event.preventDefault();
        break;
      case nextImageKey:
        onNextImage({ location, totalImages });
        event.preventDefault();
        break;
    }
  };

  const handleThumbnailFocus = (event: SyntheticEvent<HTMLElement>) =>
    setThumbnailsHaveKeyboardFocus(isKeyboardTriggeredEvent(event));

  const handleClick = (event: MouseEvent<HTMLAnchorElement>, imageIndex: number) => {
    event.preventDefault();
    setCurrentImage({
      imageIndex,
      navigationType: 'click',
      location,
    });
  };

  return (
    <div
      aria-label={PRODUCT_IMAGES_SECTION_LABEL_PLURAL}
      aria-orientation="horizontal"
      css={[styles.root, thumbnailsHaveKeyboardFocus ? styles.thumbnailsWithKeyboardFocus : null]}
      onBlur={() => setThumbnailsHaveKeyboardFocus(false)}
      onFocus={handleThumbnailFocus}
      ref={visibilityTrackingEnabled ? productImagesCarouselRef : null}
      role="tablist"
    >
      <ul css={styles.list}>
        {images.map(({ href: src, title }, i) => (
          <li css={styles.listItem} key={src}>
            <a
              aria-controls={`${idPrefix}${PRODUCT_IMAGES_TABPANEL_ID_PREFIX}${i}`}
              aria-label={`${title}, ${i + 1} of ${images.length}`}
              aria-selected={i === currentImage}
              css={styles.imageLink}
              href={getProductImageURL(src, 'original')}
              id={`${idPrefix}${PRODUCT_IMAGES_TAB_ID_PREFIX}${i}`}
              onClick={(event) => handleClick(event, i)}
              onKeyDown={handleKeyDown}
              ref={(elem) => {
                thumbnailRefs.current[i] = elem;
              }}
              role="tab"
              tabIndex={i === currentImage ? undefined : -1}
            >
              <Image
                alt={`${title}, thumbnail ${i + 1} of ${images.length}`}
                css={styles.image}
                src={getProductImageURL(src, 'mini')}
              />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};
